// @flow

import * as React from 'react';

import Logging from '@ta/common.logging';
import { ItemIdContext } from '@ta/tracking.interactions';

import ContinueSearchSection from './ContinueSearchSection';
import MatchingTripShelfSection from './MatchingTripShelfSection';
import ShelfAttractionProductSection from './ShelfAttractionProductSection';
import ShelfCruiseSearchSection from './ShelfCruiseSearchSection';
import ShelfFilterSection from './ShelfFilterSection';
import ShelfFlightsTopDestSection from './ShelfFlightsTopDestSection';
import ShelfRentalCarsSection from './ShelfRentalCarsSection';
import ShelfLinkPostSection from './ShelfLinkPostSection';
import ShelfLocationSection from './ShelfLocationSection';
import SearchResultSection from './SearchResultSection';
import ShelfTripSection from './ShelfTripSection';
import ShelfVideoSection from './ShelfVideoSection';
import type { ShelfItemType } from './types';

type Props = {
  item: ShelfItemType,
  /** function for updating the modal state for parent */
  updateModalState: boolean => void,
  /** a unique key for the shelf containing the shelfitem */
  shelfKey?: string,
  /** true if we need the version that matches other items. common use case is in a mixed shelf */
  matchOtherItems?: boolean,
};

/**
 * Single item in a shelf. The typed passed in can be one of many __typename
 * so we delegate to the appropriate component here
 */
const ShelfItem = (props: Props) => {
  const { item, updateModalState, shelfKey, matchOtherItems } = props;
  const { object, sponsorship } = item;
  const isSponsored = sponsorship && sponsorship.userProfile !== undefined;

  if (!object || !object.__typename) {
    return null;
  }

  let uniqueKey = 'shelfKey';
  if (shelfKey) {
    uniqueKey = shelfKey;
  }

  // Accommodation prices
  const isAccommodation = object && object.placeType === 'ACCOMMODATION';
  const comparisonRate =
    isAccommodation &&
    object.detail &&
    object.detail.hotelOffers &&
    object.detail.hotelOffers.comparisonRate
      ? object.detail.hotelOffers.comparisonRate
      : 0;

  // Right now only LocationInformation is supported.
  // Maybe some other types will be added here in the future.
  let componentSections;
  switch (object.__typename) {
    case 'LocationInformation':
      componentSections = (
        <ShelfLocationSection
          location={object}
          title={item.title}
          defaultSnippet={item.defaultSnippet}
          comparisonRate={comparisonRate}
        />
      );
      break;
    case 'CruiseInfoCruiseSearchInfo':
      componentSections = <ShelfCruiseSearchSection cruiseSearchInfo={object} />;
      break;
    case 'Trip':
      componentSections = matchOtherItems ? (
        <MatchingTripShelfSection trip={object} isSponsored={isSponsored} />
      ) : (
        <ShelfTripSection trip={object} />
      );
      break;
    case 'LinkPost':
      componentSections = <ShelfLinkPostSection post={object} isSponsored={isSponsored} />;
      break;
    case 'LocationFilterShelf':
      componentSections = <ShelfFilterSection filterItem={object} />;
      break;
    case 'Video':
      componentSections = (
        <ShelfVideoSection
          video={item}
          updateModalState={updateModalState}
          uniqueKey={uniqueKey}
          isSponsored={isSponsored}
        />
      );
      break;
    case 'TopDestShelfInfo':
      componentSections = <ShelfFlightsTopDestSection topDestShelfInfo={object} />;
      break;
    case 'RentalCarsLeadOffer':
      componentSections = <ShelfRentalCarsSection rentalCarsLeadOffer={object} />;
      break;
    case 'CategorySearch':
      componentSections = item.title ? (
        <ContinueSearchSection
          result={object}
          title={item.title}
          shelfKey={shelfKey || 'SearchResultsShelf'}
        />
      ) : null;
      break;
    case 'SearchResult':
      componentSections = item.title ? (
        <SearchResultSection
          result={object}
          title={item.title}
          shelfKey={shelfKey || 'SearchResultsShelf'}
        />
      ) : null;
      break;
    case 'AttractionProductInformation':
      componentSections = <ShelfAttractionProductSection location={object} />;
      break;
    default:
      Logging.error(`Unknown Social component: ${object.__typename}`);
      return null;
  }
  if (item.id) {
    return (
      <ItemIdContext.Provider value={{ itemId: item.id }}>
        <React.Fragment>{componentSections}</React.Fragment>
      </ItemIdContext.Provider>
    );
  }
  return <React.Fragment>{componentSections}</React.Fragment>;
};

ShelfItem.defaultProps = {
  member: undefined,
  shelfKey: undefined,
};

export default ShelfItem;
